import { useState } from "react";
import { Panel } from "@/components/ui/Panel";
import { StatusPill } from "@/components/ui/StatusPill";
import { DisclosureChevron, useDisclosure } from "@/components/ui/Disclosure";

/**
 * One review panel as an accordion: a header carrying the title and the
 * review tag, a chevron that says it folds, and a body that folds away by
 * itself the moment the panel's yes stands — reviewed content asks for no
 * more attention, and the fold hands the eye to what still does. The fold is
 * a convenience, not a lock: the header always reopens it.
 *
 * The header is the control rather than the chevron alone, because the header
 * is three words and reads as a good accessible name. A row whose header is a
 * title and a shelf of pills labels a chevron beside it instead.
 */
export function ReviewSection({
  title,
  reviewed,
  tagTestId,
  children,
}: {
  title: string;
  reviewed: boolean;
  tagTestId: string;
  children: React.ReactNode;
}) {
  const { open, setOpen, trigger, panel } = useDisclosure(true);
  // Adjusted while rendering rather than in an effect: the fold answers the
  // moment the yes lands, so an effect would draw the panel open once and
  // shut it on a second pass.
  const [wasReviewed, setWasReviewed] = useState(reviewed);
  if (reviewed !== wasReviewed) {
    setWasReviewed(reviewed);
    if (reviewed) setOpen(false);
  }

  return (
    <Panel className="mt-4">
      <button
        {...trigger}
        className="flex w-full cursor-pointer items-center gap-3"
      >
        <DisclosureChevron open={open} className="text-muted-foreground" />
        <span className="text-sm font-bold tracking-wide">{title}</span>
        <StatusPill
          tone={reviewed ? "success" : "warning"}
          className="ml-auto"
          data-testid={tagTestId}
        >
          {reviewed ? "✓ Reviewed" : "Please review"}
        </StatusPill>
      </button>
      <div {...panel}>{children}</div>
    </Panel>
  );
}
